/* eslint-env jest */
import { computeTextLines, getLineHeightMultiplier } from './textFieldSizing';

// Mock a linear "font": each char is `charWidth` px wide. Tests set charWidth
// via a closure so different scenarios can vary text width vs maxWidth.
const linearMeasure = (charWidth) => (t) => t.length * charWidth;

// Trivial greedy wrap matching pose_renderer._greedy_wrap: pack words within
// maxWidth, one word per line if the word itself is too wide.
const greedyWrap = (measure) => (text, maxWidth) => {
  const words = text.split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const trial = current ? `${current} ${word}` : word;
    if (!current || measure(trial) <= maxWidth) current = trial;
    else { lines.push(current); current = word; }
  });
  if (current) lines.push(current);
  return lines;
};

describe('computeTextLines', () => {
  describe('sizing_rule = wrap', () => {
    it('always wraps, even if the content would fit on one line', () => {
      // 'wrap' takes the biggest wrap-fits size in EC2; this helper just
      // decides "wrap or not" -- for 'wrap', always yes.
      const m = linearMeasure(10);
      expect(computeTextLines('wrap', 'Hi There', 1000, m, greedyWrap(m))).toEqual(['Hi There']);
      // Wide content still wraps.
      expect(computeTextLines('wrap', 'Aspire Langston Hughes Academy', 100, m, greedyWrap(m)))
        .toEqual(['Aspire', 'Langston', 'Hughes', 'Academy']);
    });
  });

  describe('sizing_rule = single_line_or_wrap', () => {
    it('keeps content on one line when it fits max width', () => {
      // '5TH GRADE' at 10px/char = 90px, maxWidth 300 -> fits single line.
      const m = linearMeasure(10);
      expect(computeTextLines('single_line_or_wrap', '5TH GRADE', 300, m, greedyWrap(m)))
        .toEqual(['5TH GRADE']);
    });

    it('wraps only when content is genuinely too wide for one line', () => {
      // 'Aspire Langston Hughes Academy' = 30 chars * 10 = 300px, maxWidth 100.
      const m = linearMeasure(10);
      expect(computeTextLines('single_line_or_wrap', 'Aspire Langston Hughes Academy', 100, m, greedyWrap(m)))
        .toEqual(['Aspire', 'Langston', 'Hughes', 'Academy']);
    });

    it('wraps content with an over-wide single word (no silent drop)', () => {
      // The lone word overflows -- greedyWrap keeps it on its own line rather
      // than returning []. This is the EC2 behavior we're mirroring.
      const m = linearMeasure(10);
      const lines = computeTextLines('single_line_or_wrap', 'Antidisestablishmentarianism', 100, m, greedyWrap(m));
      expect(lines).toEqual(['Antidisestablishmentarianism']);
    });

    it('boundary: content EXACTLY at maxWidth stays on one line (<= not <)', () => {
      const m = linearMeasure(10);
      // 'ABCDEFGHIJ' = 10 chars * 10 = 100, maxWidth 100 -> should NOT wrap.
      expect(computeTextLines('single_line_or_wrap', 'ABCDEFGHIJ', 100, m, greedyWrap(m)))
        .toEqual(['ABCDEFGHIJ']);
    });
  });

  describe('every other rule', () => {
    // 'fixed', 'shrink_to_fit', 'fit_to_bounds_max_first', blank, unknown -
    // all render as one line in the preview (EC2 shrinks the font instead of
    // wrapping). Verify none of them accidentally trigger a wrap path.
    it.each(['fixed', 'shrink_to_fit', 'fit_to_bounds_max_first', '', undefined, 'garbage_value'])(
      'never wraps for sizing_rule=%p',
      (rule) => {
        const m = linearMeasure(10);
        // Content wider than max would still not wrap for these rules.
        expect(computeTextLines(rule, 'Aspire Langston Hughes Academy', 50, m, greedyWrap(m)))
          .toEqual(['Aspire Langston Hughes Academy']);
      },
    );
  });

  describe('edge cases', () => {
    it('treats null/undefined content as empty string', () => {
      const m = linearMeasure(10);
      expect(computeTextLines('wrap', null, 100, m, greedyWrap(m))).toEqual(['']);
      expect(computeTextLines('single_line_or_wrap', undefined, 100, m, greedyWrap(m))).toEqual(['']);
      expect(computeTextLines('fixed', null, 100, m, greedyWrap(m))).toEqual(['']);
    });
  });
});

describe('getLineHeightMultiplier', () => {
  describe('rule-based defaults (no explicit override)', () => {
    it('is 0.9 for single_line_or_wrap (aggressive-tight display-text spacing)', () => {
      expect(getLineHeightMultiplier('single_line_or_wrap')).toBe(0.9);
    });

    it.each(['wrap', 'fixed', 'shrink_to_fit', 'fit_to_bounds_max_first', '', undefined, 'anything_else'])(
      'is 1.25 for sizing_rule=%p (preview historical default)',
      (rule) => {
        expect(getLineHeightMultiplier(rule)).toBe(1.25);
      },
    );
  });

  describe('explicit override wins', () => {
    it('returns the explicit value on any rule when it is a positive number', () => {
      expect(getLineHeightMultiplier('single_line_or_wrap', 0.85)).toBe(0.85);
      expect(getLineHeightMultiplier('wrap', 1.4)).toBe(1.4);
      expect(getLineHeightMultiplier('fixed', 1.1)).toBe(1.1);
    });

    it('accepts numeric strings (form inputs)', () => {
      expect(getLineHeightMultiplier('single_line_or_wrap', '0.85')).toBe(0.85);
      expect(getLineHeightMultiplier('wrap', '1.4')).toBe(1.4);
    });

    it.each([null, undefined, '', 'garbage', 0, -0.5, NaN])(
      'falls back to the rule default for bad value %p',
      (bad) => {
        expect(getLineHeightMultiplier('single_line_or_wrap', bad)).toBe(0.9);
        expect(getLineHeightMultiplier('wrap', bad)).toBe(1.25);
      },
    );
  });
});
